import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../Map.module.css";
import SimpleHashMapEngine from "../mapengine/SimpleHashMapEngine";
import InternalHashMapEngine from "../mapengine/InternalHashMapEngine";

export type ItemType = "landmark" | "food";

export interface CityItem {
  id: number;
  label: string;
  type: ItemType;
}

export interface CityEntry {
  id: number;
  city: string;
  items: CityItem[];
}

type ViewMode = "simple" | "internal";

const BUCKET_COUNT = 5;

const HashMapWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<CityEntry[]>([]);
  const [searchCityId, setSearchCityId] = useState<number | null>(null);
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [highlightCityId, setHighlightCityId] = useState<number | null>(null);
  const [highlightItemIds, setHighlightItemIds] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("simple");
  const [newCityName, setNewCityName] = useState("");
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemType, setNewItemType] = useState<ItemType>("landmark");
  const [newItemCityId, setNewItemCityId] = useState<number | null>(null);

  const [operationNote, setOperationNote] = useState(
    "Generate a random map or add your own cities and items, then search by city to see how the hash map jumps straight to that row."
  );
  const timersRef = useRef<number[]>([]);
  const nextIdRef = useRef<number>(1);
  const getNextId = () => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    return id;
  };

  const clearTimers = () => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
    setActiveLine(null);
  };

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const hashKey = (city: string): number => {
    const sum = city.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return sum % BUCKET_COUNT;
  };

  const searchCity = useMemo(
    () => entries.find((e) => e.id === searchCityId) ?? null,
    [entries, searchCityId]
  );

  useEffect(() => {
    if (entries.length === 0) {
      setSearchCityId(null);
      setNewItemCityId(null);
      return;
    }
    setSearchCityId((prev) =>
      prev && entries.some((e) => e.id === prev) ? prev : entries[0].id
    );
    setNewItemCityId((prev) =>
      prev && entries.some((e) => e.id === prev) ? prev : entries[0].id
    );
  }, [entries]);

  const handleGenerateRandom = () => {
    clearTimers();
    setHighlightCityId(null);
    setHighlightItemIds([]);

    const demo: CityEntry[] = [
      {
        id: getNextId(),
        city: "San Francisco",
        items: [
          { id: getNextId(), label: "Golden Gate Bridge", type: "landmark" },
          { id: getNextId(), label: "Ferry Building", type: "landmark" },
          { id: getNextId(), label: "Mission-style burrito", type: "food" },
        ],
      },
      {
        id: getNextId(),
        city: "Tokyo",
        items: [
          { id: getNextId(), label: "Shibuya Crossing", type: "landmark" },
          { id: getNextId(), label: "Asakusa", type: "landmark" },
          { id: getNextId(), label: "Sushi", type: "food" },
        ],
      },
      {
        id: getNextId(),
        city: "New York",
        items: [
          { id: getNextId(), label: "Times Square", type: "landmark" },
          { id: getNextId(), label: "Central Park", type: "landmark" },
          { id: getNextId(), label: "New York–style pizza", type: "food" },
        ],
      },
      {
        id: getNextId(),
        city: "Paris",
        items: [
          { id: getNextId(), label: "Eiffel Tower", type: "landmark" },
          { id: getNextId(), label: "Louvre", type: "landmark" },
          { id: getNextId(), label: "Croissant", type: "food" },
        ],
      },
    ];

    setEntries(demo);
    setOperationNote(
      "Random hash map generated. Choose a city and run a search to see its key node and value list highlighted."
    );
  };

  const handleReset = () => {
    clearTimers();
    setEntries([]);
    setSearchCityId(null);
    setHighlightCityId(null);
    setHighlightItemIds([]);
    setNewCityName("");
    setNewItemLabel("");
    setNewItemType("landmark");
    setNewItemCityId(null);
    setOperationNote(
      "Map cleared. Generate a random example or add your own cities and items."
    );
    setViewMode("simple");
  };

  const handleAddCity = () => {
    const trimmed = newCityName.trim();
    if (!trimmed) return;

    const exists = entries.some(
      (e) => e.city.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setOperationNote(
        `City "${trimmed}" already exists. Try adding items to it instead.`
      );
      return;
    }

    const entry: CityEntry = {
      id: getNextId(),
      city: trimmed,
      items: [],
    };

    setEntries((prev) => [...prev, entry]);
    setNewCityName("");
    setOperationNote(`Added city "${trimmed}". Now attach items to it.`);
  };

  const handleAddItem = () => {
    if (!newItemCityId) return;
    const trimmed = newItemLabel.trim();
    if (!trimmed) return;

    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== newItemCityId) return entry;
        return {
          ...entry,
          items: [
            ...entry.items,
            {
              id: getNextId(),
              label: trimmed,
              type: newItemType,
            },
          ],
        };
      })
    );

    setNewItemLabel("");
    setOperationNote(
      `Added a new ${newItemType === "landmark" ? "landmark" : "food"} to the selected city.`
    );
  };

  const handleDeleteItem = (cityId: number, itemId: number) => {
    clearTimers();
    setHighlightCityId(null);
    setHighlightItemIds([]);

    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== cityId) return entry;
        return {
          ...entry,
          items: entry.items.filter((i) => i.id !== itemId),
        };
      })
    );
    setOperationNote("Item removed from the hash map for that city.");
  };

  const handleSearch = () => {
    if (!searchCity) return;

    clearTimers();
    const bucketIndex = hashKey(searchCity.city);

    if (viewMode === "simple") {
      setActiveLine(1);    
      const t1 = window.setTimeout(() => {
        setActiveLine(9);
        setHighlightCityId(searchCity.id);
      }, 400);
      timersRef.current.push(t1);

      searchCity.items.forEach((item, idx) => {
        const t = window.setTimeout(() => {
          setActiveLine(11);
          setHighlightItemIds((prev) => [...prev, item.id]);
        }, 900 + idx * 450);
        timersRef.current.push(t);
      });
    }

    if (viewMode === "internal") {
      setActiveLine(1);
      const t1 = window.setTimeout(() => {
        setActiveLine(8);
        setHighlightCityId(null);
      }, 400);
      timersRef.current.push(t1);

      const bucket = entries.filter(
        (e) => hashKey(e.city) === bucketIndex
      );

      bucket.forEach((entry, idx) => {
        const t = window.setTimeout(() => {
          setActiveLine(10); 
        }, 900 + idx * 450);
        timersRef.current.push(t);
      });
    }
  };

  const beginnerCode = [
    "function hash(city) {",
    "  let sum = 0;",
    "  for (let i = 0; i < city.length; i++) {",
    "    sum += city.charCodeAt(i);",
    "  }",
    "  return sum % 5;",
    "}",
    "",
    "function search(cityObject) {",
    "  const bucketIndex = hash(cityObject.city);",
    "  highlightCity(cityObject.id);",
    "  for (let item of cityObject.items) {",
    "    highlightValue(item.id);",
    "  }",
    "}"
  ];

  const internalCode = [
    "function hashKey(city) {",
    "  let sum = 0;",
    "  for (let i = 0; i < city.length; i++) {",
    "    sum += city.charCodeAt(i);",
    "  }",
    "  return sum % BUCKET_COUNT;",
    "}",
    "",
    "function searchInternal(city) {",
    "  const index = hashKey(city);",
    "  highlightBucket(index);",
    "  for (let entry of buckets[index]) {",
    "    highlightEntry(entry.id);",
    "    if (entry.city === city) {",
    "      for (let item of entry.items)",
    "        highlightValue(item.id);",
    "    }",
    "  }",
    "}"
  ];

  return (
    <div className={styles.mapPageContainer}>
      <button className={styles.returnButton} onClick={() => navigate(-1)}>
        ← Return
      </button>

      <h1 className={styles.title}>Hash Map: Cities → Landmarks &amp; Food</h1>

      <p className={styles.description}>
        Think of each <strong>city</strong> as a key and all of its{" "}
        <strong>landmarks &amp; foods</strong> as that key’s value list.
        A hash map lets us jump directly to the row for that city using a hash
        function — instead of scanning the entire table.
      </p>

      <div className={styles.useCase}>
        <h2>Use Case:</h2>
        Hash maps power fast lookups in caches, dictionaries, configuration maps,
        and database indexes.
      </div>

      <div className={styles.timeComplexity}>
        <h2>Time Complexity</h2>
        Hash maps run in <strong>O(1)</strong> average time because the hash
        function jumps directly to the correct bucket.
      </div>

      <div className={styles.mapControlsRow}>
        <div className={styles.mapControlsGroup}>
          <h3>1. Build the Map</h3>

          <div className={styles.buttonRow}>
            <button onClick={handleGenerateRandom}>Generate Random</button>
            <button onClick={handleReset}>Reset</button>
          </div>

          <div className={styles.inlineForm}>
            <label>
              New City:
              <input
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
              />
            </label>
            <button onClick={handleAddCity}>Add City</button>
          </div>

          <div className={styles.inlineForm}>
            <label>
              City:
              <select
                value={newItemCityId ?? ""}
                onChange={(e) =>
                  setNewItemCityId(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                {entries.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.city}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Type:
              <select
                value={newItemType}
                onChange={(e) => setNewItemType(e.target.value as ItemType)}
              >
                <option value="landmark">Landmark</option>
                <option value="food">Food</option>
              </select>
            </label>

            <label>
              Label:
              <input
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
              />
            </label>

            <button onClick={handleAddItem}>Add Item</button>
          </div>
        </div>

        <div className={styles.mapControlsGroup}>
          <h3>2. Run a Lookup</h3>

          <div className={styles.inlineForm}>
            <label>
              City to search:
              <select
                value={searchCityId ?? ""}
                onChange={(e) =>
                  setSearchCityId(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                {entries.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.city}
                  </option>
                ))}
              </select>
            </label>

            <button onClick={handleSearch}>Search</button>
          </div>

          <p className={styles.operationNote}>{operationNote}</p>

          <div className={styles.hashNote}>
            <strong>Hash function:</strong> h(city) =
            (sum of character codes) mod {BUCKET_COUNT}
          </div>
        </div>
      </div>

      <div className={styles.mapCard}>
        <div className={styles.viewTabsRow}>
          <button
            className={
              viewMode === "simple" ? styles.viewTabActive : styles.viewTab
            }
            onClick={() => setViewMode("simple")}
          >
            Beginner View
          </button>
          <button
            className={
              viewMode === "internal" ? styles.viewTabActive : styles.viewTab
            }
            onClick={() => setViewMode("internal")}
          >
            Internal Storage
          </button>
        </div>

        {viewMode === "simple" ? (
          <>
            <h3 className={styles.mapTabTitle}>Key → Values</h3>
            <SimpleHashMapEngine
              entries={entries}
              highlightCityId={highlightCityId}
              highlightItemIds={highlightItemIds}
              onDeleteItem={handleDeleteItem}
            />

            <div className={styles.codeBlockCard}>
              <h3 className={styles.codeBlockTitle}>Beginner View Code</h3>
              <pre className={styles.codeBlock}>
                {beginnerCode.map((line, idx) => (
                  <div
                    key={idx}
                    className={
                      activeLine === idx + 1
                        ? styles.codeActiveLine
                        : styles.codeLine
                    }
                  >
                    {line}
                  </div>
                ))}
              </pre>
            </div>
          </>
        ) : (
          <>
            <h3 className={styles.mapTabTitle}>Buckets (Internal View)</h3>
            <p className={styles.smallHint}>
              This shows how cities are grouped into {BUCKET_COUNT} buckets.
            </p>
            <InternalHashMapEngine
              entries={entries}
              bucketCount={BUCKET_COUNT}
              hashKey={hashKey}
              activeCity={searchCity}
            />

            <div className={styles.codeBlockCard}>
              <h3 className={styles.codeBlockTitle}>Internal Storage Code</h3>
              <pre className={styles.codeBlock}>
                {internalCode.map((line, idx) => (
                  <div
                    key={idx}
                    className={
                      activeLine === idx + 1
                        ? styles.codeActiveLine
                        : styles.codeLine
                    }
                  >
                    {line}
                  </div>
                ))}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HashMapWrapper;
